<script>
import labService from "@/services/lab";
import labRoutineService from "@/services/lab-routine";

export default {
  name: "LabSelect",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    readonly: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
  },
  data() {
    return {
      labs: [],
      allLabs: [],
      isLoading: false,
      labRoutineOptions: [],
      isLoadingRoutines: false,
      selectedRoutine: null,
      selectedInternal: [],
      previousSelections: [],
      routinesWithLabs: [], // Almacenar rutinas con sus labs
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.selectedInternal = [...newVal];
      },
    },
  },

  methods: {
    async loadCatalog() {
      this.isLoading = true;
      try {
        this.labs = await labService.getLabs();
        this.allLabs = [...this.labs]; // Mantener copia de todos los labs
      } catch (error) {
        console.error("Error loading labs:", error);
        this.labs = [];
        this.allLabs = [];
      } finally {
        this.isLoading = false;
      }
    },

    async loadRoutines() {
      this.isLoadingRoutines = true;
      try {
        const routines = await labRoutineService.getLabRoutines();

        this.routinesWithLabs = routines;

        this.labRoutineOptions = [
          { id: null, name: "Sin rutina" },
          ...routines.map((routine) => ({
            id: routine._id,
            name: routine.name,
            description: routine.description,
            code: routine.code,
          })),
        ];
      } catch (error) {
        console.error("Error loading lab routines:", error);
        this.$emit("error", error);

        this.labRoutineOptions = [{ id: null, name: "Sin rutina" }];
      } finally {
        this.isLoadingRoutines = false;
      }

      this.inferRoutineFromSelection();
    },

    // La rutina no se guarda en la indicación (solo las determinaciones), así
    // que al montar con determinaciones ya elegidas (p. ej. al recuperar un
    // borrador) se deduce: si coinciden exactamente con las de una rutina, se
    // muestra esa. Solo al cargar, para no cambiarla mientras se edita.
    inferRoutineFromSelection() {
      if (this.selectedRoutine || !this.selectedInternal.length) return;
      const selectedIds = [...this.selectedInternal].sort();
      const routine = this.routinesWithLabs.find((r) => {
        const routineIds = (r.labs || []).map((lab) => lab._id).sort();
        return (
          routineIds.length === selectedIds.length &&
          routineIds.every((id, index) => id === selectedIds[index])
        );
      });
      if (routine) this.selectedRoutine = routine._id;
    },

    searchLabs(searchTerm) {
      if (!searchTerm) {
        this.labs = this.mergeWithSelected(this.allLabs);
        return;
      }

      const query = searchTerm.toLowerCase();
      const filtered = this.allLabs.filter(
        (lab) =>
          lab.description.toLowerCase().includes(query) ||
          String(lab.code).toLowerCase().includes(query)
      );
      this.labs = this.mergeWithSelected(filtered);
    },

    mergeWithSelected(list) {
      const listIds = new Set(list.map((l) => l._id));
      const missing = this.selectedInternal
        .filter((id) => !listIds.has(id))
        .map(
          (id) =>
            this.allLabs.find((l) => l._id === id) ||
            this.$store.state.cacheLabs?.[id]
        )
        .filter(Boolean);
      return [...list, ...missing];
    },

    getDescription(id) {
      // Buscar por _id en lugar de code
      const lab =
        this.labs.find((lab) => lab._id === id) ||
        this.allLabs.find((lab) => lab._id === id);
      return lab ? lab.description : "";
    },

    removeSelection(code) {
      this.selectedInternal = this.selectedInternal.filter(
        (val) => val !== code
      );
      this.$emit("input", this.selectedInternal);
    },

    selectByRoutine() {
      if (!this.selectedRoutine) {
        this.selectedInternal = [];
        this.loadCatalog();
        this.$emit("input", this.selectedInternal);
        return;
      }

      const routine = this.routinesWithLabs.find(
        (r) => r._id === this.selectedRoutine
      );

      if (routine && routine.labs) {
        // Usar _id en lugar de code
        const routineLabIds = routine.labs.map((lab) => lab._id);

        this.selectedInternal = [...routineLabIds];

        this.labs = this.mergeWithSelected(this.allLabs);

        routine.labs.forEach((lab) => {
          if (!this.$store.state.cacheLabs?.[lab._id]) {
            this.$store.dispatch("addLabs", lab);
          }
        });

        this.$emit("input", this.selectedInternal);
      }
    },

    onInput(newVal) {
      this.selectedInternal = [...newVal];

      newVal.forEach((id) => {
        // Buscar por _id en lugar de code
        const lab =
          this.labs.find((m) => m._id === id) ||
          this.allLabs.find((m) => m._id === id);
        if (lab && !this.$store.state.cacheLabs?.[id]) {
          this.$store.dispatch("addLabs", lab);
        }
      });
      this.$emit("input", this.selectedInternal);
    },
  },

  created() {
    this.loadCatalog();
    this.loadRoutines();
  },
};
</script>

<template lang="pug">
div
  label.label Rutina
  el-select(
    v-model="selectedRoutine"
    filterable
    placeholder="Seleccionar rutina"
    :disabled="isDisabled"
    :loading="isLoadingRoutines"
    @input="selectByRoutine"
  )
    el-option(v-for="routine in labRoutineOptions" :key="routine.id" :label="routine.name" :value="routine.id")

  label.label Determinación
  el-select(
    :value="selectedInternal"
    @input="onInput"
    multiple
    filterable
    collapse-tags
    :loading="isLoading"
    placeholder="Seleccionar"
    :disabled="isDisabled"
    :filter-method="searchLabs"
  )
    el-option(v-for="lab in labs" :key="lab._id" :label="lab.description" :value="lab._id")
      span(style="float: left") {{ lab.description }}

  div(v-if="selectedInternal.length > 0" class="selected-container")
    div(v-for="id in selectedInternal" :key="id" class="selection-item")
      span {{ getDescription(id) }}
      button.button-invisible-new(@click="removeSelection(id)" :disabled="isDisabled")
        micon(:name="'clear'")
</template>
